import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  EntityState,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';

import { AppError } from '@vk-hr-tek/core/error';
import { showCustomNotification } from '@vk-hr-tek/core/notifications';

import { CompanyEventsStat, CompanyGroupUsers } from '@app/gen/settings';
import { ThunkExtra } from '@app/store';
import { Entity } from '@app/types';
import {
  EventType,
  EventTypeService,
  GetCollectionResponse,
  GetCompanyCollectionsEventTypesResponse,
  GetCollectionsQuery,
} from '@app/gen/event-type';

import { SettingsState, SettingsWithRootState } from '../../settings.state';

export const getCatalogCollections = createAsyncThunk<
  GetCollectionResponse,
  GetCollectionsQuery,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getCatalogCollections',
  async (getCollectionsDto, { rejectWithValue, extra: { inject } }) => {
    try {
      const eventTypeService = inject(EventTypeService);

      const result = await eventTypeService.getCollections({
        query: getCollectionsDto,
      });

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getCatalogEventTypes = createAsyncThunk<
  GetCompanyCollectionsEventTypesResponse,
  { company_id: string; collection_id: string },
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getCatalogEventTypes',
  async (
    { company_id, collection_id },
    { rejectWithValue, extra: { inject } },
  ) => {
    try {
      const eventTypeService = inject(EventTypeService);

      const result = await eventTypeService.getCompanyEventTypesByCollection({
        query: {
          company_id,
        },
        params: {
          collection_id,
        },
      });

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateCatalogEventTypes = createAsyncThunk<
  void,
  {
    company_id: string;
    collection_id: string;
    body: { event_types: EventType[]; collection: string };
  },
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateCatalogEventTypes',
  async (
    { company_id, collection_id, body },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const eventTypeService = inject(EventTypeService);

      await eventTypeService.updateEventTypesByCollection({
        query: {
          company_id,
        },
        params: {
          collection_id,
        },
        body: {
          event_types: body.event_types,
        },
      });

      dispatch(
        showCustomNotification([
          {
            title: 'Коллекция успешно изменена',
            message: body.collection,
            type: 'info',
          },
        ]),
      );

      dispatch(
        getCatalogEventTypes({
          company_id,
          collection_id,
        }),
      );
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            title: 'Не удалось сохранить коллекцию',
            message: 'Попробуйте еще раз',
            type: 'error',
          },
        ]),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const settingsEventTypeCatalogReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<Entity<CompanyEventsStat | CompanyGroupUsers>, string> &
      SettingsState
  >,
) => {
  builder.addCase(getCatalogCollections.pending, (state) => {
    state.collections.status = 'loading';
    state.collections.error = null;
    state.collections.items = [];
  });
  builder.addCase(getCatalogCollections.fulfilled, (state, { payload }) => {
    state.collections.status = 'complete';
    state.collections.error = null;
    state.collections.items = payload.collections;
  });
  builder.addCase(
    getCatalogCollections.rejected,
    (state, { payload, error }) => {
      state.collections.status = 'failed';
      state.collections.error =
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
      state.collections.items = [];
    },
  );

  builder.addCase(getCatalogEventTypes.pending, (state) => {
    state.eventTypesList.status = 'loading';
    state.eventTypesList.error = null;
    state.eventTypesList.items = [];
  });
  builder.addCase(getCatalogEventTypes.fulfilled, (state, { payload }) => {
    state.eventTypesList.status = 'complete';
    state.eventTypesList.error = null;
    state.eventTypesList.items = payload.event_types || [];
    state.eventTypesList.collectionName = payload.collection.name;
  });
  builder.addCase(
    getCatalogEventTypes.rejected,
    (state, { payload, error }) => {
      state.eventTypesList.status = 'failed';
      state.eventTypesList.error =
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
      state.eventTypesList.items = [];
    },
  );
};
